import React, { useState, useEffect } from 'react';
import './Contact.css';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [showModal, setShowModal] = useState(false);
  const [clock, setClock] = useState(new Date());

  const token = localStorage.getItem('token');

  /* Live clock for the editorial rail */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* Esc closes modal */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    if (showModal) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showModal]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await fetch('https://api.avtechbmt.cloud/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setShowModal(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setStatus({
          type: 'error',
          message: data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      setStatus({
        type: 'error',
        message: 'Unable to connect to the server. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => setShowModal(false);

  return (
    <div className="av-contact-page">
      {/* Paper grain overlay */}
      <div className="av-grain-overlay" aria-hidden="true" />

      {/* Fixed editorial side rails */}
      <div className="av-editorial-rail av-editorial-rail-left">
        <span className="av-rail-text">AVTECH · CONTACT</span>
        <span className="av-rail-line" />
        <span className="av-rail-text">
          {clock.toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
      <div className="av-editorial-rail av-editorial-rail-right">
        <span className="av-rail-text">BARAMATI · IN</span>
        <span className="av-rail-line" />
        <span className="av-rail-text">↓</span>
      </div>

      {/* Giant background typography */}
      <div className="av-bg-giant-type" aria-hidden="true">
        <span>SAY HELLO</span>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="av-modal-overlay" onClick={closeModal}>
          <div className="av-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="av-modal-close-icon" onClick={closeModal} aria-label="Close">
              ✕
            </button>
            <div className="av-modal-tag">// Message received</div>
            <div className="av-modal-icon-wrapper">
              <svg className="av-modal-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2>
              Message <em>Sent.</em>
            </h2>
            <p className="av-modal-highlight-msg">
              Your message has been sent successfully to <strong>AV Tech Pvt. Ltd.</strong> We will
              contact you shortly via email and call.
            </p>
            <p className="av-modal-sub-desc">
              Thank you for reaching out. Our team is reviewing your inquiry and will connect
              with you as soon as possible.
            </p>
            <button className="av-modal-close-btn" onClick={closeModal}>
              <span>Got it, thank you</span>
              <span className="av-modal-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      {/* ==============================
          HERO
         ============================== */}
      <header className="av-contact-hero">
        <div className="av-contact-hero-inner">
          <div className="av-hero-badge">
            <span className="av-badge-dot"></span>
            <span className="av-tag">05 / Get in touch</span>
          </div>
          <h1 className="av-contact-hero-title">
            Let's build<br />
            <em>together.</em>
          </h1>
          <p className="av-hero-sub">
            Have a project in mind? Reach out to our team of engineers at Baramati —
            we respond within 24 hours.
          </p>

          {/* Editorial side meta */}
          <div className="av-hero-meta-row">
            <div className="av-hero-meta-item">
              <span className="av-meta-label">Response</span>
              <span className="av-meta-value">&lt; 24 hrs</span>
            </div>
            <div className="av-hero-meta-item">
              <span className="av-meta-label">Status</span>
              <span className="av-meta-value av-meta-value-live">
                <span className="av-live-dot" /> Available
              </span>
            </div>
            <div className="av-hero-meta-item">
              <span className="av-meta-label">Location</span>
              <span className="av-meta-value">Baramati, IN</span>
            </div>
          </div>
        </div>
      </header>

      {/* ==============================
          MAIN: INFO + FORM
         ============================== */}
      <section className="av-contact-main">
        <div className="av-contact-container">
          <div className="av-contact-grid">
            {/* Info sidebar */}
            <aside className="av-contact-info">
              <div className="av-info-header">
                <span className="av-tag">Direct lines</span>
              </div>

              <div className="av-info-card">
                <div className="av-info-card-header">
                  <span className="av-info-num">01</span>
                  <h3>Call us</h3>
                </div>
                <p className="av-info-detail">
                  <a href="tel:+917030330387" className="av-contact-link">
                    +91 7030330387
                  </a>
                </p>
              </div>

              <div className="av-info-card">
                <div className="av-info-card-header">
                  <span className="av-info-num">02</span>
                  <h3>Email us</h3>
                </div>
                <p className="av-info-detail">
                  <a href="mailto:avtechbaramati@gmail.com" className="av-contact-link">
                    avtechbaramati@gmail.com
                  </a>
                </p>
              </div>

              <div className="av-info-card">
                <div className="av-info-card-header">
                  <span className="av-info-num">03</span>
                  <h3>Visit us</h3>
                </div>
                <p className="av-info-detail">Shahu Lane - 2, near Creative Institute.</p>
                <p className="av-info-detail">Pragatinagar, Baramati.</p>
              </div>

              <div className="av-info-card">
                <div className="av-info-card-header">
                  <span className="av-info-num">04</span>
                  <h3>Working hours</h3>
                </div>
                <p className="av-info-detail">
                  <strong>Mon – Fri</strong> · 9:00 AM – 7:00 PM
                </p>
                <p className="av-info-detail">
                  <strong>Sat</strong> · 9:00 AM – 3:00 PM
                </p>
              </div>

              <div className="av-info-availability">
                <span className="av-status-dot"></span>
                <span className="av-status-text">Available for inquiries</span>
              </div>
            </aside>

            {/* Form */}
            <div className="av-contact-form-wrapper">
              <div className="av-form-header">
                <span className="av-tag">// Send a brief</span>
                <h2 className="av-form-title">
                  Tell us about <em>your project.</em>
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="av-contact-form">
                {status.message && (
                  <div className={`av-form-alert ${status.type === 'success' ? 'av-success' : ''}`}>
                    {status.message}
                  </div>
                )}

                <div className="av-form-row">
                  <div className="av-form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="av-form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="av-form-row">
                  <div className="av-form-group">
                    <label htmlFor="phone">Phone number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="+91…"
                    />
                  </div>

                  <div className="av-form-group">
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="What's this about?"
                    />
                  </div>
                </div>

                <div className="av-form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows="5"
                    placeholder="Tell us about your project…"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="av-cta-primary av-submit-btn"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? 'Sending…' : 'Send message'}</span>
                  <svg className="av-btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3 8h10M9 4l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          MAP
         ============================== */}
      <section className="av-contact-map-section">
        <div className="av-map-inner">
          <div className="av-map-header">
            <span className="av-tag">// Find us</span>
            <h2 className="av-map-title">
              On the <em>map.</em>
            </h2>
          </div>
          <div className="av-map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3790.898126034018!2d74.5891389!3d18.1686063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc3a1b547a423a1%3A0x5644234c1cf6d804!2sAvtech%20Pvt%20Ltd.%20Baramati!5e0!3m2!1sen!2sin!4v1784199736280!5m2!1sen!2sin"
              width="100%"
              height="480"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              title="Avtech Pvt Ltd Location Map"
            ></iframe>
            <div className="av-map-caption">
              <span className="av-map-caption-num">18.1686° N</span>
              <span className="av-map-caption-num">74.5891° E</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          FAQ
         ============================== */}
      <section className="av-contact-faq">
        <div className="av-contact-faq-inner">
          <div className="av-faq-header">
            <span className="av-tag">06 / FAQ</span>
            <h2 className="av-faq-title">
              Quick <em>answers.</em>
            </h2>
          </div>

          <div className="av-faq-grid">
            <div className="av-faq-item">
              <div className="av-faq-num">01</div>
              <h4>What's your response time?</h4>
              <p>We typically respond within 24 hours during business days.</p>
            </div>
            <div className="av-faq-item">
              <div className="av-faq-num">02</div>
              <h4>Do you offer free consultations?</h4>
              <p>Yes, we offer an initial discovery call for all potential projects.</p>
            </div>
            <div className="av-faq-item">
              <div className="av-faq-num">03</div>
              <h4>What's your pricing model?</h4>
              <p>We work on both fixed-price and time-based engagements.</p>
            </div>
            <div className="av-faq-item">
              <div className="av-faq-num">04</div>
              <h4>Do you work with startups?</h4>
              <p>Absolutely. We specialize in helping businesses scale their products.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}